// Whether the app draws its phone layout (Poulpe-624).
//
// - A touch phone gets it: a narrow viewport whose pointer is coarse. That
//   covers the iPhone's shell, a phone's browser and a Home Screen install.
//   A narrow desktop window, with docked developer tools or zoomed to 200 %,
//   keeps the desktop layout, which reflows (WCAG 1.4.10). A phone held
//   sideways is wider, and keeps the desktop's too (the N6 lead, 4 Nov).
// - On a phone, the person may choose the full board instead ("Full board"),
//   and come back. The choice is this device's layout preference, kept in
//   its storage; it holds no answer and nothing of the board.
import { useSyncExternalStore } from 'react'

/** The viewports the phone layout takes: a touch phone's, in points, with room to spare. */
export const PHONE_QUERY = '(max-width: 640px) and (pointer: coarse)'

/** The query's list, made once per `matchMedia`, so that a render reads it rather than asking again. */
let made: { from: typeof window.matchMedia; list: MediaQueryList } | null = null

function query(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  if (made?.from !== window.matchMedia) made = { from: window.matchMedia, list: window.matchMedia(PHONE_QUERY) }
  return made.list
}

function subscribe(changed: () => void): () => void {
  const list = query()
  if (!list) return () => {}
  list.addEventListener('change', changed)
  return () => list.removeEventListener('change', changed)
}

/** Whether the viewport is a touch phone's now; false where the browser cannot say. */
export function isPhoneWidth(): boolean {
  return query()?.matches ?? false
}

/** Whether the viewport is a touch phone's, following it as it changes. */
export function usePhone(): boolean {
  return useSyncExternalStore(subscribe, isPhoneWidth, () => false)
}

/** Where this device keeps its choice of the full board on a phone. */
export const LAYOUT_KEY = 'poulpe.layout'

const listeners = new Set<() => void>()

function readFull(): boolean {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'full'
  } catch {
    return false
  }
}

/** Choose the full board (true) or the phone's screens (false) on this device's phone layout. */
export function chooseFullBoard(full: boolean): void {
  try {
    if (full) localStorage.setItem(LAYOUT_KEY, 'full')
    else localStorage.removeItem(LAYOUT_KEY)
  } catch {
    // Storage refused: the layout stays as the viewport chooses it.
  }
  for (const listener of listeners) listener()
}

/** Whether this device chose the full board on a phone. */
export function useFullBoard(): boolean {
  return useSyncExternalStore((changed) => { listeners.add(changed); return () => listeners.delete(changed) }, readFull, () => false)
}
